import React from 'react';
import { logger } from '@/app/utils/logger';
import styles from './DataList.module.scss';

interface DataItem {
  label: string;
  value: React.ReactNode;
  copyable?: boolean;
  /** When value is a link or other node, copy this string instead. */
  copyText?: string;
}

interface DataListProps {
  items: DataItem[];
  columns?: 1 | 2;
  className?: string;
}

/**
 * DataList - Displays key-value pairs in a structured, readable format
 * Used for transaction context display instead of raw JSON
 */
export const DataList: React.FC<DataListProps> = ({
  items,
  columns = 1,
  className = '',
}) => {
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      logger.error('Failed to copy to clipboard');
    }
  };

  const copyTextForItem = (item: DataItem): string | null => {
    if (item.copyText) {
      return item.copyText;
    }
    if (typeof item.value === 'string') {
      return item.value;
    }
    return null;
  };

  return (
    <dl className={`${styles.dataList} ${styles[`columns${columns}`]} ${className}`}>
      {items.map((item, index) => (
        <div key={index} className={styles.dataItem}>
          <dt className={styles.label}>{item.label}</dt>
          <dd className={styles.value}>
            {item.value || <span className={styles.empty}>—</span>}
            {item.copyable && copyTextForItem(item) && (
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => {
                  const text = copyTextForItem(item);
                  if (text) {
                    void handleCopy(text);
                  }
                }}
                title="Copy to clipboard"
              >
                📋
              </button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

// Convenience component for displaying a single value with label
interface DataFieldProps {
  label: string;
  value: React.ReactNode;
  copyable?: boolean;
}

export const DataField: React.FC<DataFieldProps> = ({ label, value, copyable }) => {
  return <DataList items={[{ label, value, copyable }]} />;
};

export default DataList;

